import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import { MDSModal } from "@/mds/components/overlays/modal/modal";
import { MDSModalZIndex } from "@/mds/components/overlays/modal/modal.types";
import { MDSText, MDSTextVariant, MDSIconKind, MDSIconButton } from "@/mds";
import { SettingsTabBar } from "@/pages/settings/components/tab-bar";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import {
  SettingsNotificationPreferencesPage,
  SettingsVoiceModePage,
  SettingsChatPage,
  SettingsSubscriptionManagementPage,
  SettingsReferralProgramPage,
  SettingsDebugAccountPage,
  SettingsCalendarPage,
  SettingsTemplatesPage,
  SettingsAppearancePage,
  SettingsEditorPage,
  SettingsChromeExtensionPage,
  SettingsEmailToMemPage,
  SettingsZapierPage,
  SettingsApiKeysPage,
  SettingsMcpPage,
  SettingsConnectedToolsPage,
  SettingsMemAgentCommunicationChannelsPage,
  SettingsMemAgentPage,
  SettingsProfilePage,
  SettingsExportsModalPage,
  SettingsImportsModalPage,
  SettingsUsagePage,
} from "@/pages/settings";
import { SettingsDebugSandboxPage } from "@/pages/settings/settings-debug-sandbox-page";

const renderSettingsContent = (activeTab: SettingsTabId) => {
  switch (activeTab) {
    case SettingsTabId.NotificationPreferencesPage:
      return <SettingsNotificationPreferencesPage />;
    case SettingsTabId.VoiceModePage:
      return <SettingsVoiceModePage />;
    case SettingsTabId.ChatPage:
      return <SettingsChatPage />;
    case SettingsTabId.SubscriptionManagementPage:
      return <SettingsSubscriptionManagementPage />;
    case SettingsTabId.ReferralProgramPage:
      return <SettingsReferralProgramPage />;
    case SettingsTabId.ExportsPage:
      return <SettingsExportsModalPage />;
    case SettingsTabId.ImportsPage:
      return <SettingsImportsModalPage />;
    case SettingsTabId.DebugAccountPage:
      return <SettingsDebugAccountPage />;
    case SettingsTabId.DebugSandboxPage:
      return <SettingsDebugSandboxPage />;
    case SettingsTabId.CalendarAndMeetingsPage:
      return <SettingsCalendarPage />;
    case SettingsTabId.TemplatesPage:
      return <SettingsTemplatesPage />;
    case SettingsTabId.AppearancePage:
      return <SettingsAppearancePage />;
    case SettingsTabId.EditorPage:
      return <SettingsEditorPage />;
    case SettingsTabId.ChromeExtensionPage:
      return <SettingsChromeExtensionPage />;
    case SettingsTabId.EmailToMemPage:
      return <SettingsEmailToMemPage />;
    case SettingsTabId.ZapierPage:
      return <SettingsZapierPage />;
    case SettingsTabId.ApiKeysPage:
      return <SettingsApiKeysPage />;
    case SettingsTabId.McpPage:
      return <SettingsMcpPage />;
    case SettingsTabId.ConnectedToolsPage:
      return <SettingsConnectedToolsPage />;
    case SettingsTabId.MemAgentPage:
      return <SettingsMemAgentPage />;
    case SettingsTabId.MemAgentCommunicationChannelsPage:
      return <SettingsMemAgentCommunicationChannelsPage />;
    case SettingsTabId.ProfilePage:
      return <SettingsProfilePage />;
    case SettingsTabId.UsagePage:
      return <SettingsUsagePage />;
    default:
      return <SettingsNotificationPreferencesPage />;
  }
};

export const SettingsModal = observer(() => {
  const { store } = useAppStore();
  const { settingsModal } = store;
  const shouldSuppressForTakeoverModal =
    store.platformInfo.welcomeToProModalIsOpen ||
    store.platformInfo.welcomeToProactiveModalIsOpen ||
    store.subscriptions.memProactiveRedirectModalIsActive ||
    store.memAgent.isSlackConnectedModalOpen;
  const isReminderShillSetupDialogOpen = settingsModal.isReminderShillSetupDialogOpen;

  if (!settingsModal.isOpen || shouldSuppressForTakeoverModal) return null;

  return (
    <MDSModal
      isOpen
      isDismissable={!isReminderShillSetupDialogOpen}
      handleClose={() => settingsModal.close()}
      zIndex={MDSModalZIndex.BelowMentionsMenu}
    >
      <StyledModalContainer data-mds-name="settings-modal-content">
        <StyledHeader>
          <MDSText variant={MDSTextVariant.HeaderL}>Settings</MDSText>
          <MDSIconButton
            iconKind={MDSIconKind.Exit}
            onClick={() => settingsModal.close()}
            aria-label="Close settings"
          />
        </StyledHeader>
        <StyledBody>
          <StyledSidebar>
            <SettingsTabBar
              sections={settingsModal.tabBarSections}
              selectedItemId={settingsModal.activeTab}
            />
          </StyledSidebar>
          <StyledContent>{renderSettingsContent(settingsModal.activeTab)}</StyledContent>
        </StyledBody>
      </StyledModalContainer>
    </MDSModal>
  );
});

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 1004px;
  max-width: calc(100vw - 48px);
  height: 85dvh;
  max-height: 845px;
  background: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  overflow: hidden;
  container-type: inline-size;
  container-name: mds-panel;

  @container mds-panel (max-width: 750px) {
    width: 100vw;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[7]} ${theme.spacing[7]} ${theme.spacing[4]}`};
  flex-shrink: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  @container mds-panel (max-width: 750px) {
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  }
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @container mds-panel (max-width: 750px) {
    flex-direction: column;
  }
`;

const StyledSidebar = styled.div`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 248px;
  padding: ${({ theme }) => theme.spacing[2]};
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow-y: auto;
  scrollbar-width: none;
  margin: 0;

  @container mds-panel (max-width: 750px) {
    width: 100%;
    border-right: 0;
    overflow-y: visible;
    margin: 0;
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  }
`;

const StyledContent = styled.div`
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  min-height: 0;
  padding: 0;
`;
